import { usePlatformInfo } from '@acsbe/ui';
import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react';
import { CmsDomain, OwnershipTokenContext } from './types';

export const APPLICATION_MODES = {
  DEFAULT: 'LICENSE_DIRECT_ACCESS' as const,
  PARTNER: 'PARTNER' as const,
  LIMITED: 'LIMITED' as const,
};

type APPLICATION_MODE =
  | typeof APPLICATION_MODES.DEFAULT
  | typeof APPLICATION_MODES.PARTNER
  | typeof APPLICATION_MODES.LIMITED;

export const VIEW_STATES = {
  BLANK: 'BLANK' as const,
  SIGNUP: 'SIGNUP' as const, // default view
  LOGIN: 'LOGIN' as const,
  CHOOSE_DOMAIN: 'CHOOSE_DOMAIN' as const,
  DASHBOARD: 'DASHBOARD' as const,
  TRIAL_STARTED: 'TRIAL_STARTED' as const,
  BILLING: 'BILLING' as const,
  PHONE_NUMBER: 'PHONE_NUMBER' as const,
  ANIMATION_SCREEN: 'ANIMATION_SCREEN' as const,
  NO_EMAIL_SIGNUP_DEFAULT: 'NO_EMAIL_SIGNUP_DEFAULT' as const,
  CALENDLY: 'CALENDLY' as const,
};

export type VIEW_STATE =
  | typeof VIEW_STATES.BLANK
  | typeof VIEW_STATES.SIGNUP
  | typeof VIEW_STATES.LOGIN
  | typeof VIEW_STATES.CHOOSE_DOMAIN
  | typeof VIEW_STATES.PHONE_NUMBER
  | typeof VIEW_STATES.DASHBOARD
  | typeof VIEW_STATES.TRIAL_STARTED
  | typeof VIEW_STATES.BILLING
  | typeof VIEW_STATES.ANIMATION_SCREEN
  | typeof VIEW_STATES.NO_EMAIL_SIGNUP_DEFAULT
  | typeof VIEW_STATES.CALENDLY;

export type UIFlags = {
  showPlanComparisonView: boolean;
};
interface ApplicationViewState {
  applicationViewState: VIEW_STATE;
  setApplicationViewState: (viewState: VIEW_STATE) => void;
  applicationMode: APPLICATION_MODE;
  setApplicationMode: (mode: APPLICATION_MODE) => void;
  isDomainAvailable: boolean;
  setIsDomainAvailable: (show: boolean) => void;
  signupWizardStep: SignupWizardSteps;
  authFlowNavigations: {
    goToLogin: () => void;
    goToSignup: () => void;
    goToChooseDomain: () => void;
    goToPhoneNumber: () => void;
    goToDefaultAuthScreen: () => void;
    goToTrialStarted: () => void;
    goToAnimationScreen: () => void;
    goToNoEmailSignupDefault: () => void;
    goToCalendly: () => void;
  };
  SignupWizard: {
    stepOneCompleted: (
      fullName: string,
      email: string,
      password: string,
    ) => void;
    stepOneCompletedWithSocial: (tempSocialLoginId: string) => void;
    stepTwoCompleted: (
      domainSelected: CmsDomain,
      available: boolean,
      ownershipTokenContext?: OwnershipTokenContext,
    ) => void;
    stepThreeCompleted: (countryCode: string, phoneNumber: string) => void;
    backToStepOne: () => void;
    backToStepTwo: () => void;
    resetSignupWizard: () => void;
  };
  showPlanComparisonView: boolean;
  setShowPlanComparisonView: (show: boolean) => void;
}

interface SignupWizardSteps {
  Step1: {
    completed: boolean;
    fullName?: string;
    email?: string;
    password?: string;
    tempSocialLoginId?: string;
  };
  Step2: {
    completed: boolean;
    domainSelected?: CmsDomain;
    available?: boolean;
    ownershipTokenContext?: OwnershipTokenContext;
  };
  Step3: {
    completed: boolean;
    phoneNumber?: string;
    countryCode?: string;
  };
}

const ApplicationViewStateContext = createContext<
  ApplicationViewState | undefined
>(undefined);

const ApplicationViewStateProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { uptoTablet } = usePlatformInfo();

  const [applicationViewState, setApplicationViewState] = useState<VIEW_STATE>(
    VIEW_STATES.BLANK,
  );
  const [applicationMode, setApplicationMode] = useState<APPLICATION_MODE>(
    APPLICATION_MODES.DEFAULT,
  );
  const [isDomainAvailable, setIsDomainAvailable] = useState(false);

  const [defaultAuthScreen, setDefaultAuthScreen] = useState<VIEW_STATE>(
    VIEW_STATES.SIGNUP,
  );

  const [signupWizardStep, setSignupWizardStep] = useState<SignupWizardSteps>({
    Step1: {
      completed: false,
    },
    Step2: {
      completed: false,
    },
    Step3: {
      completed: false,
    },
  });

  const [showPlanComparisonView, setShowPlanComparisonView] =
    useState<boolean>(!uptoTablet);

  // Update showPlanComparisonView based on uptoDesktop breakpoint
  useEffect(() => {
    setShowPlanComparisonView(!uptoTablet);
  }, [uptoTablet]);

  const authFlowNavigations = {
    goToLogin: () => {
      setApplicationViewState(VIEW_STATES.LOGIN);
      setDefaultAuthScreen(VIEW_STATES.LOGIN);
    },
    goToSignup: () => {
      setApplicationViewState(VIEW_STATES.SIGNUP);
      setDefaultAuthScreen(VIEW_STATES.SIGNUP);
    },
    goToChooseDomain: () => setApplicationViewState(VIEW_STATES.CHOOSE_DOMAIN),
    goToPhoneNumber: () => setApplicationViewState(VIEW_STATES.PHONE_NUMBER),
    goToDefaultAuthScreen: () => setApplicationViewState(defaultAuthScreen),
    goToTrialStarted: () => setApplicationViewState(VIEW_STATES.TRIAL_STARTED),
    goToAnimationScreen: () => {
      setApplicationViewState(VIEW_STATES.ANIMATION_SCREEN);
    },
    goToNoEmailSignupDefault: () => {
      setApplicationViewState(VIEW_STATES.NO_EMAIL_SIGNUP_DEFAULT);
    },
    goToCalendly: () => setApplicationViewState(VIEW_STATES.CALENDLY),
  };

  const SignupWizard = {
    stepOneCompleted: (fullName: string, email: string, password: string) => {
      setSignupWizardStep((prev) => ({
        ...prev,
        Step1: {
          completed: true,
          fullName,
          email,
          password,
        },
      }));
    },
    stepOneCompletedWithSocial: (tempSocialLoginId: string) => {
      setSignupWizardStep((prev) => ({
        ...prev,
        Step1: {
          completed: true,
          tempSocialLoginId,
        },
      }));
    },
    stepTwoCompleted: (
      domainSelected: CmsDomain,
      available: boolean,
      ownershipTokenContext?: OwnershipTokenContext,
    ) => {
      setSignupWizardStep((prev) => ({
        ...prev,
        Step2: {
          completed: true,
          domainSelected,
          available,
          ownershipTokenContext,
        },
      }));
    },
    stepThreeCompleted: (countryCode: string, phoneNumber: string) => {
      setSignupWizardStep((prev) => ({
        ...prev,
        Step3: {
          completed: true,
          countryCode,
          phoneNumber,
        },
      }));
    },
    backToStepOne: () => {
      setSignupWizardStep((prev) => ({
        ...prev,
        Step1: {
          ...prev.Step1,
          completed: false,
        },
        Step2: {
          completed: false,
        },
        Step3: {
          completed: false,
        },
      }));
    },
    backToStepTwo: () => {
      setSignupWizardStep((prev) => ({
        ...prev,
        Step2: {
          completed: false,
          lmsToken: undefined,
          domain: undefined,
        },
        Step3: {
          completed: false,
        },
      }));
    },
    resetSignupWizard: () => {
      setIsDomainAvailable(false);
      setSignupWizardStep({
        Step1: {
          completed: false,
        },
        Step2: {
          completed: false,
        },
        Step3: {
          completed: false,
        },
      });
    },
  };

  return (
    <ApplicationViewStateContext.Provider
      value={{
        applicationViewState,
        setApplicationViewState,
        applicationMode,
        setApplicationMode,
        isDomainAvailable,
        setIsDomainAvailable,
        authFlowNavigations,
        SignupWizard,
        signupWizardStep,
        showPlanComparisonView,
        setShowPlanComparisonView,
      }}
    >
      {children}
    </ApplicationViewStateContext.Provider>
  );
};

const useApplicationViewState = () => {
  const context = useContext(ApplicationViewStateContext);
  if (context === undefined) {
    throw new Error(
      'useApplicationViewState must be used within an ApplicationViewStateProvider',
    );
  }
  return context;
};

export { ApplicationViewStateProvider, useApplicationViewState };
